import { createRef } from 'react';
import { fetchRequest } from './';
import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit';
import { collectionChanges } from './change-api';
import { getAutoCompleteInput, inputValueSelection } from './controls';
import { dateDiff } from './date-time';
import { Audit, Provider, TrackingCode, WorksheetId } from './domain';
import { formApi, FormApi, providerField } from './form';
import { listApi } from './list';
import { WorksheetContext } from './visit-context';
import { isReviewWorkflowState } from './workflow-api';
import { WorksheetFormApi } from './worksheet-form-api';
import { worksheetMessage, WorksheetResult } from './worksheet-service';

export type TrackingCodeResult = WorksheetResult<TrackingCode>;
export type TrackingCodeListResult = WorksheetResult<TrackingCode[]>;

const service = 'worksheettracking';
const method = 'post';

export const addTrackingCodeRequest = fetchRequest<
  TrackingCode,
  TrackingCodeResult
>({
  method,
  body: (args: TrackingCode) =>
    worksheetMessage(args, 'addtrackingcode', service),
});

export const updateTrackingCodeRequest = fetchRequest<
  TrackingCode,
  TrackingCodeResult
>({
  method,
  body: (args: TrackingCode) =>
    worksheetMessage(args, 'updatetrackingcode', service),
});

export const getTrackingCodesRequest = fetchRequest<
  WorksheetId,
  TrackingCodeResult
>({
  method,
  body: (args: WorksheetId) =>
    worksheetMessage(args, 'gettrackingcodes', service),
  notFoundResult: () => ({ result: [] }),
});

interface TrackingCodeIds extends WorksheetId {
  trackingCodeId: string;
}
export type TrackingCodeIdsResult = WorksheetResult<TrackingCodeIds>;

export const deleteTrackingCodeRequest = fetchRequest<
  TrackingCodeIds,
  TrackingCodeIdsResult
>({
  method,
  body: (args: TrackingCode) =>
    worksheetMessage(args, 'deletetrackingcode', service),
});


interface TrackingCodeParams extends TrackingCode {
  startTime?: Date;
  endTime?: Date;
}

export interface TrackingCodeApi extends WorksheetFormApi<TrackingCodeParams> {}

export function trackingCodeApi(context: WorksheetContext): TrackingCodeApi {
  const { worksheet, status, config, tracking, readonly } = context;
  const { worksheetId, owningProvider } = worksheet;
  const { providers, trackingCodesList } = config;

  const defaultFilter = isReviewWorkflowState() ? 'all' : 'active';
  const list = listApi({
    defaultFilter,
    all: tracking,
    id: (a: TrackingCode) => a.code,
    equals: (a, b) => a.code === b.code,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
  });

  let editing: TrackingCode;
  function editItem(v: TrackingCode) {
    api.onEdit();
    list.actions.edit(v);
    editing = v;
    form.setValues({
      code: code.setListValue((a: { code: string }) => a.code === v.code),
      startDateTime: v.startDateTime,
      endDateTime: v.endDateTime,
      quantity: v.quantity,
      provider: provider.setListValue(
        (u: Provider) => u?.userId === v?.provider?.userId
      ),
      notes: v.notes,
    });
  }

  function addItem(v: TrackingCode) {
    async function request(update?: (v: any) => void) {
      if (update) {
        update(v);
      }
      v.trackingCodeId = editing?.trackingCodeId;
     
      const response = await (editing
        ? updateTrackingCodeRequest(v)
        : addTrackingCodeRequest(v));
      if (response.success) {
        const item = response.result;

        if (update) {
          update(item);
        }
        if (editing) {
          list.actions.replace(
            item,
            (v) => v.trackingCodeId === editing.trackingCodeId
          );
        } else {
          list.actions.add(item);
        }

        focusInput('');
      } else {
        setStatus((response.errors || []).join(' '));
      }

      editing = null;
    }

    const edit = list.data.getState().editing;
    if (edit) {
      reviewChangesOr(
        {
          title: 'Review Changes For Tracking Code ',
          original: edit,
          update: v,
          changeReasons: {
            actualCPTCode: config.auditDownCode,
            targetCPTCode: config.auditDownCode,
          },
          fieldNames: ['reason', 'provider', 'code'],
          onComplete: (a: Audit) => {
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    } else {
      reviewActionOr(
        {
          title: 'Add Tracking Code ',
          list: context.config.auditTracking as any,
          onComplete: (a) => {
            a.action = 'CREATE';

            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    }
  }

  function removeItem(v: TrackingCode) {
    async function request() {
      const response = await deleteTrackingCodeRequest({
        worksheetId: worksheet.worksheetId,
        trackingCodeId: v.trackingCodeId,
      });
      if (response.success) {
        list.actions.remove(v);
      } else {
        setStatus((response.errors || []).join(' '));
      }
    }

    reviewActionOr(
      {
        title: 'Remove Tracking Code  ',
        list: context.config.auditTracking as any,
        onComplete: (a) => {
          a.action = 'DELETE';
          v.audit = a;
          request();
        },
      },
      request
    );
  }

  const initialValues = {
    code: null,
    startDateTime: null,
    endDateTime: null,
    quantity: 1,
    provider: owningProvider,
    notes: '',
  };

  const setStatus = status.getState().setStatus;

  //TODO extract from workflow list item, dynamically update required
  let dateRequired = false;

  function normalizeDateRange(st?: any, en?: any) {
    const start = st || startDateTime.value.getState().value;
    const end = en || endDateTime.value.getState().value;

    try {
      if (start && end) {
        const r = dateDiff(start, end, true);

        if (r) {
          form.setValues({
            startDateTime: r.startDate,
            endDateTime: r.endDate,
          });
          endDateTime.value.setState({ value: r.endDate });
        }
      }
    } catch (er) {
      console.log('normalize error', er);
    }
  }
  const form: FormApi<Partial<TrackingCode>> = formApi({
    disabled: readonly,
    values: initialValues,
    fields: {
      code: {
        label: 'Other Tracking',
        list: trackingCodesList,
        listLabel: (v) => v.description,
        required: true,
        inputRef: createRef(),
        onEnterValid: () => {
          form.submit();
        },
      },
      startDateTime: {
        label: 'Start Date',
        required: dateRequired,
        onChange: (v) => {
          normalizeDateRange(v);
        },
        onEnter: () => {
          form.submit();
        },
      },
      endDateTime: {
        label: 'End Date',
        required: dateRequired,
        onChange: (v) => {
          normalizeDateRange(null, v);
        },
        onEnter: () => {
          form.submit();
        },
      },
      provider: providerField(providers, {
        onEnterValid: () => {
          form.submit();
        },
      }),
      quantity: {
        label: 'Quantity',
        min: 1,
        max: 10,
        style: { width: '40px' },
      },
      notes: {
        label: 'Notes',
        onEnter: () => form.submit(),
      },
    },
    valid: (v) => {
      if (dateRequired) {
        console.log('date required');
      }
      return v.code;
    },
    resetValues: () => initialValues,
    onSubmit: async (v) => {
      const args = {
        worksheetId,
        code: v.code.code,
        description: v.code.description,
        startDateTime: v.startDateTime,
        endDateTime: v.endDateTime,
        quantity: v.quantity || 1,
        provider: v.provider,
        notes: (v.notes || '').trim(),
      };

      addItem(args);
    },
  });

  const { controls } = form;
  const { provider, code, startDateTime, endDateTime } = controls;

  const inputElement = getAutoCompleteInput(code.inputRef);
  const focusInput = inputValueSelection(inputElement);

  const listChanges = collectionChanges(list, {
    changeLabel: (v: TrackingCode[]) => v.map((u) => u.code).join(', '),
  });

  function setPhysician(userId: string) {
    const defaultProvider = providers.filter(
      (v: Provider) => v.userId === userId
    )[0];

    if (defaultProvider) {
      initialValues.provider = defaultProvider;
    }
    provider.setListValue((v: Provider) => v.userId === userId);
  }

  const api = {
    form,
    list,
    listChanges,
    context,
    addItem,
    editItem,
    removeItem,
    setPhysician,
    onEdit: () => null,
  };
  return api;
}
